// Runtime message registry.
//
// This module holds the translation DATA at runtime but imports NONE of it
// statically. Data only enters via `registerMessages(locale, map)`. That is
// the whole trick behind the bundle-size win:
//
//   - On the SERVER, every locale's full map is registered (the [locale]
//     layout via ./server, server components via ./server-t, the SSR pass
//     of client components via lib/locale-context.tsx), so server output is
//     always complete.
//   - On the CLIENT, the LocaleProvider registers only the files the
//     current page needs (./routes): the shared core plus the page's own
//     keys, content-hashed static JSON written by
//     scripts/build-i18n-json.mjs and preloaded with the HTML. Each file
//     merges into the locale's map.
//
// A client lookup that misses a partially loaded locale asks the miss
// handler (installed by the LocaleProvider) to fetch the key's namespace
// and renders an empty string meanwhile, never the raw key; the provider
// re-renders once the namespace lands.
//
// Because nothing here `import`s az/ru/en, a client component that imports
// `t` from i18n.ts (which re-exports from here) does not drag all three
// dictionaries into its chunk.
import type { Locale } from "../i18n-config";
import type { TranslationKey } from "./keys";

export type Messages = Record<TranslationKey, string>;

// Multiple locales may legitimately be registered in one runtime (e.g. a long
// dev session that hits /az then /ru, or a theoretical page that renders two
// locales). We keep a per-locale slot rather than a single "active" map so a
// `t(key, "ru")` call never silently reads az strings.
const registry: Partial<Record<Locale, Messages>> = {};
// Locales whose whole map is registered: a miss there is a missing key.
const complete = new Set<Locale>();
// Namespaces registered whole per locale (on-demand files after a miss).
const fullNamespaces: Partial<Record<Locale, Set<string>>> = {};

/** First dot segment of a key, "_" for dotless keys; the unit of the
 *  on-demand files (scripts/i18n-scan.mjs uses the same rule). */
export function namespaceOf(key: string): string {
  const i = key.indexOf(".");
  return i === -1 ? "_" : key.slice(0, i);
}

/**
 * Register strings for a locale. A complete map (the default, used by the
 * server loaders) replaces whatever was there, so a dev edit to az.ts
 * re-registers fresh strings. A partial one (`{ namespace }` or `{}`)
 * merges into the locale's map; `namespace` records that every key of it
 * is now present.
 */
export function registerMessages(
  locale: Locale,
  messages: Partial<Messages>,
  scope: { complete?: boolean; namespace?: string } = { complete: true },
): void {
  if (scope.complete) {
    registry[locale] = messages as Messages;
    complete.add(locale);
    return;
  }
  if (complete.has(locale)) return;
  const cur = registry[locale];
  registry[locale] = cur ? Object.assign(cur, messages) : ({ ...messages } as Messages);
  if (scope.namespace) (fullNamespaces[locale] ??= new Set()).add(scope.namespace);
}

/**
 * Called on a client miss with the key's namespace. Returns true when it
 * registered the namespace synchronously (dev), false when a fetch is in
 * flight.
 */
type MissHandler = (locale: Locale, namespace: string, key: string) => boolean;
let onMiss: MissHandler | null = null;

export function setMissHandler(handler: MissHandler | null): void {
  onMiss = handler;
}

/**
 * Resolve a single key for a locale, with the SAME fallback chain the old
 * monolithic `t` used: requested locale -> en -> the key itself. The only
 * difference is that a fallback locale is only consulted if its map happens
 * to be registered; in practice the active locale is always registered, and
 * en is registered whenever an en page is (or was) rendered.
 *
 * On the client, a key outside the loaded files of a partially loaded
 * locale is not "missing" yet: its namespace is fetched (see onMiss) and
 * the lookup renders "" until it lands, so a raw key never flashes.
 */
export function resolve(key: TranslationKey, locale: Locale): string {
  const primary = registry[locale];
  const hit = primary?.[key];
  if (hit) return hit;
  if (onMiss && !complete.has(locale) && !fullNamespaces[locale]?.has(namespaceOf(key))) {
    if (!onMiss(locale, namespaceOf(key), key)) return "";
    const late = registry[locale]?.[key];
    if (late) return late;
  }
  const en = registry.en;
  const enHit = en?.[key];
  if (enHit) return enHit;
  return key;
}
